import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskSplashScreenBlockConfig } from '@front-platform/content-builder';
import { kioskSplashScreenBlockResolveConfig } from '@front-platform/content-builder';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  ViewChild,
  input,
} from '@angular/core';
import { Router } from '@angular/router';

import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import {
  kioskSplashCtaOverlayClassList,
  shouldDisplayKioskLanguageFlags,
} from '@shared/blocks/kiosk/kiosk-select-language-block/kiosk-language-flags.util';
import {
  isPlayableVideoMedia,
  resolveKioskSplashBackgroundImageUrl,
} from '@shared/utils/media/kiosk-splash-background-media.util';
export interface KioskSplashScreenBlockRuntimeConfig {}

export type KioskSplashScreenBlockConfigInput = Partial<
  Omit<KioskSplashScreenBlockConfig, keyof KioskSplashScreenBlockRuntimeConfig> &
    KioskSplashScreenBlockRuntimeConfig
>;
export type KioskSplashScreenBlockResolvedConfig = Omit<
  KioskSplashScreenBlockConfig,
  keyof KioskSplashScreenBlockRuntimeConfig
> &
  Partial<KioskSplashScreenBlockRuntimeConfig>;
export interface KioskSplashScreenBlockContract extends ConfigComponentContract<
  KioskSplashScreenBlockConfigInput,
  KioskSplashScreenBlockResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-splash-screen-block',
  templateUrl: './kiosk-splash-screen-block.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class KioskSplashScreenBlockComponent
  implements AfterViewInit, KioskSplashScreenBlockContract
{
  readonly config = input<KioskSplashScreenBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskSplashScreenBlockConfig,
    KioskSplashScreenBlockRuntimeConfig
  >(kioskSplashScreenBlockResolveConfig, () => ({ config: this.config() }));
  get labelCallToAction(): string | undefined {
    return this.resolvedConfig()['label_call_to_action'] as unknown as string | undefined;
  }
  get linkUrl(): string | undefined {
    return this.resolvedConfig()['link_url'] as unknown as string | undefined;
  }
  get displayAccessibilityButton(): boolean {
    return this.resolvedConfig()['display_accessibility_button'] as unknown as boolean;
  }
  get labelAccessibilityButton(): string | undefined {
    return this.resolvedConfig()['label_accessibility_button'] as unknown as string | undefined;
  }
  get backgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor;
  }
  get backgroundImage(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig()['background_image'] as unknown as
      IContentBuilderFieldImage | undefined;
  }
  get backgroundVideo(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig()['background_video'] as unknown as
      IContentBuilderFieldImage | undefined;
  }
  get textColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor;
  }
  get backgroundOpacity(): number | undefined {
    return this.resolvedConfig()['background_opacity'] as unknown as number | undefined;
  }

  @ViewChild('videoPlayer') bgVideo?: ElementRef<HTMLVideoElement>;

  get isVideoMedia(): boolean {
    return isPlayableVideoMedia(this.backgroundVideo);
  }

  get splashBackgroundImageUrl(): string | undefined {
    return resolveKioskSplashBackgroundImageUrl(this.backgroundVideo, this.backgroundImage);
  }

  private readonly marketplaceStoreSelector = inject(MarketplaceStoreSelector);

  protected readonly hasLanguageFlags = computed(() =>
    shouldDisplayKioskLanguageFlags(this.marketplaceStoreSelector.selectMarketplace().locales),
  );

  protected readonly ctaOverlayClassList = computed(() =>
    kioskSplashCtaOverlayClassList(this.hasLanguageFlags()),
  );

  constructor(
    private readonly router: Router,
    private readonly helperRouter: RouterHelperService,
  ) {}

  ngAfterViewInit(): void {
    const video = this.bgVideo?.nativeElement;
    if (!video) {
      return;
    }
    video.muted = true;
    video.play().catch(err => {
      console.warn('Autoplay blocked:', err);
    });
  }

  redirect(): void {
    if (!this.linkUrl) {
      return;
    }
    this.router.navigate([this.helperRouter.translateRoute(this.linkUrl)]);
  }

  onInnerClick(event: Event): void {
    event.stopPropagation();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskSplashScreenBlockComponent, {
  'keyAliases': {},
});
